import { safeStorage, StorageType } from './safeStorage';

export interface StorageOptions {
  expiryMs?: number;
}

export interface StorageConstructorOptions {
  windowIsolated?: boolean;
  storageType?: StorageType;
}

interface StoredData<T> {
  data: T;
  timestamp: number;
}

export class Storage {
  private prefix: string;
  private windowIsolated: boolean;
  private storageType: StorageType;

  constructor(prefix: string = '', options?: StorageConstructorOptions) {
    this.prefix = prefix;
    this.windowIsolated = options?.windowIsolated ?? false;
    this.storageType = options?.storageType ?? 'local';
  }

  private getKey(key: string): string {
    let finalKey = key;

    // prefix가 있으면 prefix 추가
    if (this.prefix) {
      finalKey = `${this.prefix}:${finalKey}`;
    }

    return finalKey;
  }

  set<T>(key: string, value: T, options?: StorageOptions): boolean {
    try {
      const storageKey = this.getKey(key);
      const storedData: StoredData<T> = {
        data: value,
        timestamp: Date.now(),
      };
      return safeStorage.setItem(storageKey, JSON.stringify(storedData), this.storageType);
    } catch {
      return false;
    }
  }

  get<T>(key: string, options?: StorageOptions): T | null {
    try {
      const storageKey = this.getKey(key);
      const stored = safeStorage.getItem(storageKey, this.storageType);
      if (!stored) return null;

      const storedData = JSON.parse(stored) as StoredData<T>;

      if (options?.expiryMs) {
        const age = Date.now() - storedData.timestamp;
        if (age > options.expiryMs) {
          this.remove(key);
          return null;
        }
      }

      return storedData.data;
    } catch {
      return null;
    }
  }

  remove(key: string): boolean {
    try {
      const storageKey = this.getKey(key);
      return safeStorage.removeItem(storageKey, this.storageType);
    } catch {
      return false;
    }
  }

  clear(): boolean {
    try {
      if (!safeStorage.isAvailable(this.storageType)) {
        return false;
      }

      if (typeof window === 'undefined') {
        return false;
      }

      if (this.prefix || this.windowIsolated) {
        // safeStorage를 통해 모든 키 가져오기
        const allKeys = safeStorage.getAllKeys(this.storageType);

        allKeys.forEach((key) => {
          let shouldDelete = false;

          if (this.prefix) {
            shouldDelete = key.startsWith(this.prefix + ':');
          }

          if (shouldDelete) {
            safeStorage.removeItem(key, this.storageType);
          }
        });
      } else {
        // prefix나 windowIsolated이 없으면 모든 키 삭제
        const allKeys = safeStorage.getAllKeys(this.storageType);
        allKeys.forEach((key) => {
          safeStorage.removeItem(key, this.storageType);
        });
      }
      return true;
    } catch {
      return false;
    }
  }
}

export const createStorage = (prefix?: string, options?: StorageConstructorOptions) =>
  new Storage(prefix, options);
